/**
 * Validates a single field against its schema definition
 * @param {any} value - The field value to validate
 * @param {Object} schema - Schema definition for the field
 * @returns {string|Array|null} - Error message or null if valid
 */
export const validateField = (value, schema) => {
  // Check required fields
  if (schema.required && (value === undefined || value === null || value === "")) {
    return `This field is required`;
  }

  // Skip further validation if value is undefined/null and not required
  if (value === undefined || value === null) {
    return null;
  }

  // Type checking
  switch (schema.type) {
    case "string":
      if (typeof value !== "string") {
        return `Expected string, got ${typeof value}`;
      }
      // Optional string length validation
      if (schema.minLength !== undefined && value.length < schema.minLength) {
        return `Minimum length is ${schema.minLength}`;
      }
      if (schema.maxLength !== undefined && value.length > schema.maxLength) {
        return `Maximum length is ${schema.maxLength}`;
      }
      // Optional pattern validation
      if (schema.pattern && !new RegExp(schema.pattern).test(value)) {
        return schema.patternMessage || `Invalid format`;
      }
      break;

    case "number":
      if (typeof value !== "number" && isNaN(Number(value))) {
        return `Expected number, got ${typeof value}`;
      }
      const numValue = Number(value);
      // Optional range validation
      if (schema.min !== undefined && numValue < schema.min) {
        return `Minimum value is ${schema.min}`;
      }
      if (schema.max !== undefined && numValue > schema.max) {
        return `Maximum value is ${schema.max}`;
      }
      break;

    case "boolean":
      if (typeof value !== "boolean") {
        return `Expected boolean, got ${typeof value}`;
      }
      break;

    case "array":
      if (!Array.isArray(value)) {
        return `Expected array, got ${typeof value}`;
      }
      // Optional array length validation
      if (schema.minItems !== undefined && value.length < schema.minItems) {
        return `Minimum ${schema.minItems} items required`;
      }
      if (schema.maxItems !== undefined && value.length > schema.maxItems) {
        return `Maximum ${schema.maxItems} items allowed`;
      }

      // Validate array items if we have an itemSchema
      if (schema.itemSchema) {
        const itemErrors = value
          .map((item, index) => {
            const errors = validateObject(item, schema.itemSchema);
            return Object.keys(errors).length > 0 ? { index, errors } : null;
          })
          .filter(Boolean);

        if (itemErrors.length > 0) {
          return itemErrors;
        }
      }
      break;

    case "object":
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return `Expected object, got ${Array.isArray(value) ? "array" : typeof value}`;
      }
      // If nested schema is provided, validate nested object
      if (schema.properties) {
        const nestedErrors = validateObject(value, schema.properties);
        if (Object.keys(nestedErrors).length > 0) {
          return nestedErrors;
        }
      }
      break;

    default:
      break;
  }

  // Custom validation function if provided
  if (schema.validate && typeof schema.validate === "function") {
    try {
      const customError = schema.validate(value);
      if (customError) {
        return customError;
      }
    } catch (error) {
      return `Validation error: ${error.message}`;
    }
  }

  return null;
};

/**
 * Validates an object against a schema
 * @param {Object} obj - The object to validate
 * @param {Object} schema - Schema definition with field rules
 * @returns {Object} - Map of field names to error messages
 */
export const validateObject = (obj, schema) => {
  const errors = {};

  // Check if object exists when required
  if (!obj && schema._required) {
    return { _error: "Object is required" };
  }

  // Skip validation if object is null/undefined and not required
  if (!obj && !schema._required) {
    return errors;
  }

  // Validate all fields defined in the schema
  Object.keys(schema).forEach((fieldName) => {
    // Skip meta fields starting with underscore
    if (fieldName.startsWith("_")) return;

    const fieldSchema = schema[fieldName];
    const value = obj ? obj[fieldName] : undefined;
    const fieldError = validateField(value, fieldSchema);

    if (fieldError) {
      errors[fieldName] = fieldError;
    }
  });

  return errors;
};

/**
 * Validates an array of objects against a schema
 * @param {Array} items - Array of objects to validate
 * @param {Object} schema - Schema for each item
 * @returns {Array} - Array of validation errors with item indices
 */
export const validateArray = (items, schema) => {
  if (!Array.isArray(items)) {
    return [{ error: "Expected an array of items" }];
  }

  return items
    .map((item, index) => {
      const errors = validateObject(item, schema);
      return Object.keys(errors).length > 0 ? { index, errors } : null;
    })
    .filter(Boolean);
};

/**
 * Sanitizes an object to match a schema structure
 * @param {Object} obj - The object to sanitize
 * @param {Object} schema - Schema definition
 * @returns {Object} - Sanitized object
 */
export const sanitizeObject = (obj, schema) => {
  if (!obj) return {};

  const result = {};

  // Only include fields defined in the schema
  Object.keys(schema).forEach((fieldName) => {
    // Skip meta fields starting with underscore
    if (fieldName.startsWith("_")) return;

    const fieldSchema = schema[fieldName];
    const value = obj[fieldName];

    if (value === undefined || value === null) {
      // Set default values for required fields if specified
      if (fieldSchema.required && fieldSchema.default !== undefined) {
        result[fieldName] = fieldSchema.default;
      }
    } else if (fieldSchema.type === "array" && Array.isArray(value) && fieldSchema.itemSchema) {
      // Sanitize array items
      result[fieldName] = value.map((item) => sanitizeObject(item, fieldSchema.itemSchema));
    } else if (fieldSchema.type === "object" && typeof value === "object" && fieldSchema.properties) {
      // Sanitize nested objects
      result[fieldName] = sanitizeObject(value, fieldSchema.properties);
    } else {
      // Keep the value but convert types if needed
      if (fieldSchema.type === "number" && typeof value !== "number") {
        const numValue = Number(value);
        result[fieldName] = isNaN(numValue) ? value : numValue;
      } else {
        result[fieldName] = value;
      }
    }
  });

  return result;
};

/**
 * Sanitizes an array of objects to match a schema
 * @param {Array} items - Array of objects to sanitize
 * @param {Object} schema - Schema for each item
 * @returns {Array} - Sanitized array
 */
export const sanitizeArray = (items, schema) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => sanitizeObject(item, schema));
};
